import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Save } from "lucide-react";
import { Controller } from "react-hook-form";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showSuccess, showError } from "@/utils/toastHelpers";

export default function MedicineForm({ medicine, onClose }) {
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      company: "",
      product: "",
      strength: "",
      dosageForm: "",
      packSize: "",
      schedule: "",
      genericName: "",
      nappiCode: "",
      category: "",
      minimumStock: "",
      reorderPoint: "",
      sellingPrice: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (medicine) {
      reset({
        company: medicine.company || "",
        product: medicine.product || "",
        strength: medicine.strength || "",
        dosageForm: medicine.dosageForm || "",
        packSize: medicine.packSize || "",
        schedule: medicine.schedule || "",
        genericName: medicine.genericName || "",
        nappiCode: medicine.nappiCode || "",
        category: medicine.category || "",
        minimumStock: medicine.minimumStock || "",
        reorderPoint: medicine.reorderPoint || "",
        sellingPrice: medicine.sellingPrice || "",
        notes: medicine.notes || "",
      });
    }
  }, [medicine, reset]);

  // Create medicine mutation
  const createMedicine = useMutation({
    mutationFn: (data) => apiClient.post(ENDPOINTS.MEDICINES.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["medicines"]);
      showSuccess("Medicine created successfully");
      onClose();
    },
    onError: (error) => {
      console.error("Failed to create medicine:", error);
      showError(
        "Failed to create medicine",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  // Update medicine mutation
  const updateMedicine = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.put(ENDPOINTS.MEDICINES.UPDATE(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries(["medicines"]);
      showSuccess("Medicine updated successfully");
      onClose();
    },
    onError: (error) => {
      console.error("Failed to update medicine:", error);
      showError(
        "Failed to update medicine",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  const onSubmit = async (data) => {
    try {
      if (medicine) {
        await updateMedicine.mutateAsync({ id: medicine.id, data });
      } else {
        await createMedicine.mutateAsync(data);
      }
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-900">
            {medicine ? "Edit Medicine" : "Add New Medicine"}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company"
                {...register("company", { required: "Company is required" })}
                placeholder="e.g., Adcock"
                className={errors.company ? "border-red-500" : ""}
              />
              {errors.company && (
                <p className="text-sm text-red-600">
                  {errors.company.message}
                </p>
              )}
            </div>

            {/* Product */}
            <div className="space-y-2">
              <Label htmlFor="product">Product *</Label>
              <Input
                id="product"
                {...register("product", { required: "Product is required" })}
                placeholder="e.g., Amfexa"
                className={errors.product ? "border-red-500" : ""}
              />
              {errors.product && (
                <p className="text-sm text-red-600">
                  {errors.product.message}
                </p>
              )}
            </div>

            {/* Strength */}
            <div className="space-y-2">
              <Label htmlFor="strength">Strength *</Label>
              <Input
                id="strength"
                {...register("strength", { required: "Strength is required" })}
                placeholder="e.g., 5mg"
                className={errors.strength ? "border-red-500" : ""}
              />
              {errors.strength && (
                <p className="text-sm text-red-600">
                  {errors.strength.message}
                </p>
              )}
            </div>

            {/* Dosage Form */}
            <div className="space-y-2">
              <Label htmlFor="dosageForm">Dosage Form *</Label>
              <Input
                id="dosageForm"
                {...register("dosageForm", {
                  required: "Dosage form is required",
                })}
                placeholder="e.g., Tablets"
                className={errors.dosageForm ? "border-red-500" : ""}
              />
              {errors.dosageForm && (
                <p className="text-sm text-red-600">
                  {errors.dosageForm.message}
                </p>
              )}
            </div>

            {/* Pack Size */}
            <div className="space-y-2">
              <Label htmlFor="packSize">Pack Size *</Label>
              <Input
                id="packSize"
                {...register("packSize", { required: "Pack size is required" })}
                placeholder="e.g., 30's"
                className={errors.packSize ? "border-red-500" : ""}
              />
              {errors.packSize && (
                <p className="text-sm text-red-600">
                  {errors.packSize.message}
                </p>
              )}
            </div>

            {/* Selling Price */}
            <div className="space-y-2">
              <Label htmlFor="sellingPrice">Selling Price (per pack) *</Label>
              <Input
                id="sellingPrice"
                type="number"
                {...register("sellingPrice", {
                  required: "Selling price is required",
                  min: { value: 0, message: "Price must be positive" },
                })}
                placeholder="e.g., 150.00"
                step="0.01"
                className={errors.sellingPrice ? "border-red-500" : ""}
              />
              {errors.sellingPrice && (
                <p className="text-sm text-red-600">
                  {errors.sellingPrice.message}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Price charged to patients for invoicing
              </p>
            </div>

            {/* Schedule */}
            <div className="space-y-2">
              <Label htmlFor="schedule">Schedule</Label>
              <Input
                id="schedule"
                {...register("schedule")}
                placeholder="e.g., 6 or S4"
              />
            </div>

            {/* Generic Name */}
            <div className="space-y-2">
              <Label htmlFor="genericName">Generic Name</Label>
              <Input
                id="genericName"
                {...register("genericName")}
                placeholder="e.g., Dextroamphetamine"
              />
            </div>

            {/* NAPPI Code */}
            <div className="space-y-2">
              <Label htmlFor="nappiCode">NAPPI Code</Label>
              <Input
                id="nappiCode"
                {...register("nappiCode")}
                placeholder="e.g., 123456"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="supplement">Supplement</SelectItem>
                      <SelectItem value="hormone">Hormone</SelectItem>
                      <SelectItem value="vitamin">Vitamin</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Minimum Stock */}
            <div className="space-y-2">
              <Label htmlFor="minimumStock">Minimum Stock Level</Label>
              <Input
                id="minimumStock"
                type="number"
                {...register("minimumStock", {
                  min: { value: 0, message: "Minimum stock must be positive" },
                })}
                placeholder="e.g., 20"
                className={errors.minimumStock ? "border-red-500" : ""}
              />
              {errors.minimumStock && (
                <p className="text-sm text-red-600">
                  {errors.minimumStock.message}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Alert when total stock falls below this level
              </p>
            </div>

            {/* Reorder Point */}
            <div className="space-y-2">
              <Label htmlFor="reorderPoint">Reorder Point</Label>
              <Input
                id="reorderPoint"
                type="number"
                {...register("reorderPoint", {
                  min: { value: 0, message: "Reorder point must be positive" },
                })}
                placeholder="e.g., 30"
                className={errors.reorderPoint ? "border-red-500" : ""}
              />
              {errors.reorderPoint && (
                <p className="text-sm text-red-600">
                  {errors.reorderPoint.message}
                </p>
              )}
              <p className="text-xs text-slate-500">
                Trigger reorder when stock reaches this level
              </p>
            </div>

          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              rows={3}
              placeholder="Additional notes or storage requirements..."
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMedicine.isPending || updateMedicine.isPending}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {createMedicine.isPending || updateMedicine.isPending
                ? "Saving..."
                : medicine
                  ? "Update Medicine"
                  : "Add Medicine"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
